<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">Add Storage Details to Delivered Item</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <v-form 
          class="modal-card-body"
          :key="warehouseId" 
          :valid.sync="valid">

          <div class="level is-vcentered">
            <header class="title is-5 mb-0">
              <span v-if="addingNewStorage">
                Adding Item to New storage
              </span>
              <span v-else>
                Adding Item to Existing Storage
              </span>
            </header>
            <button 
              class="button is-small is-primary" 
              @click="addingNewStorage = !addingNewStorage">
              <span v-if="addingNewStorage">
                Add item to existing storage
              </span>
              <span v-if="!addingNewStorage">
                Add item to new storage
              </span>
            </button>
          </div>

          <template v-if="addingNewStorage">

            <div class="columns">
              <div class="column is-half">
                <label class="label">Storage Name*</label>
                <v-text-field
                  required
                  placeholder="Storage Name"
                  v-model="storage.storage_name">
                </v-text-field>
              </div>

              <div class="column is-half">
                <div class="label-and-button">
                  <label class="label">Storage Type*</label>
                  <span 
                    class="tag" 
                    @click="managingStorageTypes = true">
                    <span>Manage Storage Types</span>
                  </span>
                </div>
                <v-select
                  v-if="storageTypes.length > 0"
                  required
                  item-text="storage_type_name"
                  item-value="id"
                  :options="storageTypes"
                  v-model="storage.storage_type_id"
                />
              </div>
            </div>

            <div class="columns">
              <div class="column is-half">
                <label class="label">Warehouse*</label>
                <v-select
                  required
                  item-text="warehouse_name"
                  item-value="id"
                  :options="warehouses"
                  v-model="warehouseId"
                />
              </div>

              <div class="column is-half">
                <label class="label">Warehouse Space*</label>
                <v-select
                  v-if="warehouseId"
                  required
                  item-text="location_name"
                  item-value="id"
                  :options="warehouse.locations"
                  v-model="storage.warehouse_location_id"
                />
                <div v-else class="has-text-grey-light mt-2">
                  Please select a warehouse
                </div>
              </div>
            </div>

          </template>

          <div v-else class="field">
            <label class="label">Choose from an Existing Storage*</label>
            <v-select
              required
              :item-text="(v) => `(${v.storage_code})${v.storage_name}`"
              item-value="id"
              :options="storages"
              v-model="assignment.storage_id"
            />
          </div>

          <hr/>

          <div class="columns">
            <div class="column is-half">
              <label class="label">Quantity Available</label>
              <div v-if="orderDetail">
                {{ orderDetail.total_qty - assignment.qty }}
              </div>
            </div>
            <div class="column is-half">
              <label class="label">Quantity Already Stored</label>
              <div v-if="orderDetail">
                {{ orderDetail.qty_stored }}
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">Quantity to Assign*</label>
            <v-text-field
              v-if="orderDetail"
              required
              placeholder="Quantity to be stored in selected warehouse location"
              v-model="assignment.qty"
              :rules="[
                (v) => v <= orderDetail.total_qty || 'Cannot assign a greater quantity than what is available'
              ]">
            </v-text-field>
          </div>

        </v-form>

        <footer class="modal-card-foot">
          <button 
            :disabled="!valid"
            class="button is-primary" 
            @click.stop="save()">
            Save
          </button>
          <button 
            class="button" 
            @click.stop="close(true)">
            Cancel
          </button>
        </footer>

      </div>

      <manage-storage-types-modal 
        v-model="managingStorageTypes"/>

    </div>

  </div>
</template>

<script>
import VForm from '@/components/base/VForm'
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import ManageStorageTypesModal from '@/components/warehousing/ManageStorageTypesModal'

import { mapState, mapActions } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    },
    orderDetail: {
      type: Object
    }
  },
  components: { 
    VForm,
    VSelect,
    VTextField,
    ManageStorageTypesModal
  },
  data() {
    return {
      warehouseId: null,
      addingNewStorage: true,
      storage: {
        storage_name: null,
        storage_type_id: null,
        warehouse_location_id: null
      },
      managingStorageTypes: false,
      assignment: {
        storage_id: null,
        qty: null,
        order_detail_id: null
      },
      valid: false
    }
  },
  computed: {
    ...mapState("warehouses", ["warehouses"]),
    ...mapState("storages", ["storages"]),
    ...mapState("storageTypes", ["storageTypes"]),
    warehouse() {
      return this.warehouses.filter(d => d.id == this.warehouseId)[0];
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("order", ["assignStorage"]),
    ...mapActions("warehouses", ["getWarehouses"]),
    ...mapActions("storages", ["getStorages", "addStorage"]),
    ...mapActions("storageTypes", ["getStorageTypes"]),
    initialize() {
      if (this.storages.length == 0) {
        this.getStorages({ per_page: 1000 });
      }
      if (this.storageTypes.length == 0) {
        this.getStorageTypes({ per_page: 100 });
      }
      if (this.warehouses.length == 0) {
        this.getWarehouses({ per_page: 100 });
      }
    },
    save() {
      this.assignment.order_detail_id = this.orderDetail.id;

      if (!this.addingNewStorage) {
        this.assignStorage(this.assignment).then(() => {
          this.close(true);
        })
      } else {
        this.addStorage(this.storage).then(resp => {
          this.assignment.storage_id = resp.data.id;
          this.assignStorage(this.assignment).then(() => {
            this.close(true);
          })
        })
      }
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
.label-and-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .tag {
    cursor: pointer;
    &:hover {
      background: gainsboro;
    }
  }
}
</style>
